/**
 *  用于存放mobx中共用的数据或者逻辑
 */

import { Forter } from '@/components/Layout/ScriptSnippets';
import { browserPushUrl, formatPrice, intervalPromise, isEmpty } from '@/utils';
import { armsErrorSend } from '@/utils/libs/armsErrorSend';
import { encodeURIEncryptFE } from '@/utils/libs/JSEncrypt';

/**
 *
 * @param {Object} delivery
 * @param {String} type  账单地址billing/默认用户地址
 * @description 给地址信息绑定州代码字段provinceCode
 * @returns
 */
export const bindProvinceCode = (ProvinceOceanCodes, delivery, type) => {
  if (isEmpty(delivery) || delivery[type === 'billing' ? 'ocean_code' : 'provinceCode']) return delivery;
  const _c_code = delivery?.country_code ?? delivery?.countryCode;
  if (['US', 'CA'].includes(_c_code)) {
    const codes = ProvinceOceanCodes[_c_code];
    const item = codes.find((o) => o.province_name === delivery?.province);
    if (item) {
      delivery[type === 'billing' ? 'ocean_code' : 'provinceCode'] = item.ocean_code;
    } else {
      armsErrorSend('bindProvinceCode cannot find provinceCode', {
        filename: 'bindProvinceCode Error',
        data: { delivery, ProvinceOceanCodes, type },
      });
    }
  }
  return delivery;
};

/** 根据provinceCode绑定province */
export const bindDeliveryProvince = (ProvinceOceanCodes, delivery = {}) => {
  if (delivery?.province || isEmpty(delivery)) return delivery;
  const _c_code = delivery?.country_code ?? delivery?.countryCode ?? window?.country?.code;
  if (['US', 'CA'].includes(_c_code)) {
    const codes = ProvinceOceanCodes[_c_code];
    const item = codes.find((o) => o.ocean_code === delivery?.provinceCode);
    if (item) {
      delivery.province = item.province_name;
    }
  }
  return delivery;
};

/**
 * 规整init delivery所需要的字段
 * @param {*} delivery
 * @param {*} type req 编辑保存请求所需字段（下划线->下划线） type='formInit' 编辑快递信息表单的数据（下划线）
 * type='resToInit' 把请求响应回来的驼峰字段转成下划线 type='reqToTax' 税费请求要的请求字段
 * */
export const getDeliveryObj = (delivery = {}, type = 'req', data = {}) => {
  if (type === 'req') {
    return {
      id: delivery?.id ?? undefined,
      'lastName': delivery?.last_name,
      'firstName': delivery?.first_name,
      'phoneCode': delivery?.phone_code,
      'phoneCodeArea': delivery?.phone_code_area, // 国际手机区号地区
      'phone': delivery?.phone,
      'email': delivery?.email,
      'province': delivery?.province, // 非必填，香港、澳门、台湾不需要填写该字段
      'city': delivery?.city,
      'district': delivery?.district, // 非必填，大陆地级市没有区县以及海外不需要填
      'address': delivery?.address,
      'subAddress': delivery?.sub_address,
      'country': window?.country?.name || delivery?.country, // 从country里获取，以解决因国家名称改变而导致后端校验失败。
      'countryCode': window?.country?.code || delivery?.country_code,
      'zipCode': delivery?.zip_code,
      'isDefault': delivery?.is_default,
      'resolveAddress': delivery?.resolveAddress,
    };
  }

  if (type === 'formInit') {
    return {
      id: delivery?.id,
      country: delivery.country ?? window?.country?.name,
      country_code: delivery.country_code ?? window?.country?.code,
      email: delivery.email,
      first_name: delivery?.first_name ?? undefined,
      last_name: delivery?.last_name,
      phone_code: delivery.phone_code,
      phone: delivery?.phone,
      province: delivery?.province,
      city: delivery?.city,
      district: delivery?.district,
      address: delivery?.address,
      sub_address: delivery?.sub_address,
      zip_code: delivery?.zip_code,
    };
  }

  if (type === 'resToInit') {
    return bindDeliveryProvince(data.ProvinceOceanCodes, {
      city: delivery?.city?.replace(
        /(\w)(\w*)/g,
        (match, firstStr, restStr) => firstStr.toUpperCase() + restStr.toLowerCase()
      ),
      provinceCode: delivery?.provinceCode,
      province: delivery?.province,
      address: delivery?.address?.replace(
        /(\w)(\w*)/g,
        (match, firstStr, restStr) => firstStr.toUpperCase() + restStr.toLowerCase()
      ),
      sub_address: delivery?.subAddress,
      zip_code: delivery?.zipCode,
    });
  }

  if (type === 'reqToTax') {
    return {
      'countryCode': delivery?.country_code,
      'provinceCode': delivery?.provinceCode,
      'city': delivery?.city,
      'address': delivery?.address,
      'subAddress': delivery?.sub_address,
      'zipCode': delivery?.zip_code,
    };
  }
  return {};
};

/**
 * 计算单品的总价 价格*数量（参与计算总价的金额） 分销价 | 八折优惠价 | 现价
 * @param item
 * @param from cart购物车（默认）| order结账页 - 结账页展示的单品金额不包含分销价，分销折扣算在优惠券减价中
 */
export const handleItemPrice = (item, from = 'cart') => {
  if (from === 'cart' && item.reseller_discount_price?.amount) {
    return item.reseller_discount_price.amount * item.count;
  }
  if (item?.accessoryDiscountPrice?.amount) {
    return item.price?.amount * item.count - item.accessoryDiscountPrice.amount;
  }
  return item.price?.amount * item.count;
};

/** 计算结账页面的总金额 */
export const calcOrderConfirmPrice = ({ orderCartItems, cartExtraFee, _orderCount }) => {
  const TIMES = 1000;
  let items = 0; // 商品总价
  let ship = 0; // 运费
  let tax = 0; // 税费
  let discount = 0; // 总折扣
  let total = 0; // 合计（最终金额）
  let currency = '';
  let signal = '';
  /* 计算总价 */
  orderCartItems.forEach((item) => {
    if (isEmpty(item)) return;
    currency = item.price.currency;
    signal = item.price.signal;

    items += (handleItemPrice(item, 'order') * TIMES) / TIMES;
    if (item.bind_services && item.bind_services.length > 0) {
      item.bind_services.forEach((o) => {
        if (o.price) items += (o.price.amount * TIMES * item.count) / TIMES;
      });
    }
  });
  items = formatPrice(items);

  /* 计算运费税费 */
  if (cartExtraFee) {
    ship = cartExtraFee?.shippingFee?.amount || 0;
    tax = cartExtraFee?.tax?.amount || 0;
  }

  /* 计算订单折扣 */
  const {
    isFreeOrder,
    salesTax = 0,
    couponAmount = 0,
    giftCardAmount = 0,
    resellerCouponAmount = 0,
    tradeUpCouponAmount = 0,
    professionalCouponAmount = 0,
    paypalDiscount = 0,
  } = _orderCount;

  discount =
    (-couponAmount * TIMES -
      giftCardAmount * TIMES -
      resellerCouponAmount * TIMES -
      tradeUpCouponAmount * TIMES -
      professionalCouponAmount * TIMES) /
    TIMES;
  // 避免中间计算值出现在页面上，放入临时变量中
  let _total =
    (items * TIMES + ship * TIMES + tax * TIMES + discount * TIMES + salesTax * TIMES - paypalDiscount * TIMES) / TIMES;

  total = _total < 0 ? 0.01 : _total;
  if (isFreeOrder) {
    total = 0;
  }
  if (_orderCount.total !== total) {
    _orderCount.total = total;
  }

  return {
    items,
    ship,
    tax,
    discount,
    total,
    salesTax,
    signal,
    currency,
    paypalDiscount,
  };
};

/** 跳转订单结果页 */
export const redirectToOrderResultPage = (orderNumber) => {
  browserPushUrl(`/order/result/${encodeURIEncryptFE(orderNumber)}`);
};

/** 跳转二次支付页 */
export const redirectToOrderPayPage = (orderNumber) => {
  browserPushUrl(`/order/pay/${encodeURIEncryptFE(orderNumber)}`);
};

/**
 * forterToken可能因为SDK没有下载完或者其他原因 没有初始化成功，因此二次重试获取token。
 * @return Promise catch问题
 */
export const preCheckForterToken = async (appMobx) => {
  // 如果没有ftr-token可能是sdk错误重新下载
  if (!window.localStorage.getItem('ftr-token')) {
    await intervalPromise(
      () => {
        if (window.localStorage.getItem('ftr-token')) return true;
        Forter({ app: appMobx });
      },
      500,
      5
    ).catch((e) => {
      armsErrorSend(e, { filename: 'reload forter fail' });

      throw e;
    });
  }
};

function TaskGap() {
  // 当前执行的任务数
  let task = [];
  let timer = null;

  this.getGap = (GAP_TIME = 500) => {
    clearTimeout(timer);
    // 任务+1
    task.push(1);
    // 之前的任务数量
    const quantity = task?.length;

    // 所有任务执行后，清空队列
    timer = setTimeout(() => {
      task = [];
    }, (quantity + 1) * GAP_TIME);
    return new Promise((resolve) => {
      // 任务执行时间
      setTimeout(() => {
        resolve();
      }, (quantity - 1) * GAP_TIME);
      // 每个任务间隔300毫秒
      setTimeout(() => {
        task.shift();
      }, quantity * GAP_TIME);
    });
  };
}

export const taskGapInstance = new TaskGap();
